import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Package, Truck, CheckCircle2, Clock, AlertCircle, Loader2, MapPin, Mail, Hash, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STEPS = [
  { key: 'placed',   label: 'Order placed',   icon: CheckCircle2 },
  { key: 'paid',     label: 'Payment cleared', icon: ShieldCheck },
  { key: 'accepted', label: 'Accepted',       icon: Package },
  { key: 'shipped',  label: 'Shipped',        icon: Truck },
];

function statusIndex(order) {
  const s = (order?.status || '').toLowerCase();
  if (s === 'shipped' || s === 'delivered') return 3;
  if (s === 'accepted') return 2;
  if (s === 'paid' || order?.payment_status === 'paid' || order?.paid_at) return 1;
  return 0;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso.slice(0, 10); }
}

export default function TrackOrder() {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setOrder(null);
    if (!invoiceNumber || !email) {
      setError('Please enter both your invoice number and email.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/orders/track`, {
        invoice_number: invoiceNumber.trim().toUpperCase(),
        email: email.trim(),
      });
      setOrder(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not look up that order.');
    } finally {
      setSubmitting(false);
    }
  };

  const idx = order ? statusIndex(order) : -1;

  return (
    <div className="min-h-[calc(100vh-160px)] py-20 px-4" style={{ backgroundColor: '#f7f9f3' }} data-testid="track-order-page">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <span className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-3">Order tracking</span>
          <h1 className="text-4xl md:text-5xl font-bold font-heading mb-3">Where's my order?</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">Look up the latest status using your invoice number and the email you used at checkout.</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-6 md:p-8 mb-8">
          <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4" data-testid="track-order-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Invoice number</label>
              <div className="relative">
                <Hash className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value.toUpperCase())}
                  placeholder="INV-12345"
                  required
                  data-testid="track-invoice-input"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition font-mono uppercase"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email used at checkout</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="track-email-input"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="track-submit"
                className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-white font-medium transition bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {submitting ? 'Looking up\u2026' : 'Track my order'}
              </button>
            </div>
            {error && (
              <div className="md:col-span-2 text-sm rounded-lg px-3 py-2.5 bg-red-50 border border-red-200 text-red-700 flex items-start gap-2" data-testid="track-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </form>
        </motion.div>

        {order && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-6 md:p-8" data-testid="track-result">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-6 pb-6 border-b border-slate-200">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Invoice</div>
                <div className="text-2xl font-bold font-mono text-slate-800">{order.invoice_number}</div>
                <div className="text-xs text-slate-500 mt-1">Placed {fmtDate(order.created_at)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Order total</div>
                <div className="text-2xl font-bold text-emerald-700">£{(order.total || 0).toFixed(2)}</div>
                <div className="text-xs text-slate-500 mt-1">{order.quantity} bottle{(order.quantity ?? 0) > 1 ? 's' : ''} \u00b7 inc. VAT</div>
              </div>
            </div>

            {/* Stepper */}
            <div className="grid grid-cols-4 gap-2 mb-8" data-testid="track-status-stepper">
              {STATUS_STEPS.map((step, i) => {
                const Active = i <= idx;
                const Icon = step.icon;
                return (
                  <div key={step.key} className="flex flex-col items-center text-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${Active ? 'bg-emerald-700 text-white' : 'bg-slate-200 text-slate-400'}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className={`text-xs mt-2 font-medium ${Active ? 'text-emerald-800' : 'text-slate-400'}`}>{step.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Tracking pill */}
            {order.tracking_number ? (
              <a
                href={order.tracking_url || (String(order.carrier || '').toLowerCase().startsWith('yodel') ? `https://www.yodel.co.uk/tracking/${order.tracking_number}` : `https://www.royalmail.com/track-your-item#/tracking-results/${order.tracking_number}`)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 hover:bg-emerald-100 transition-colors"
                data-testid="track-tracking-pill"
              >
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-emerald-700" />
                  <div>
                    <div className="text-sm font-semibold text-emerald-800">{order.carrier || 'Royal Mail'} tracking</div>
                    <div className="text-xs text-emerald-700 font-mono">{order.tracking_number}</div>
                  </div>
                </div>
                <span className="text-xs text-emerald-700 font-semibold">Open tracking \u2192</span>
              </a>
            ) : (
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="track-no-tracking">
                <Clock className="w-5 h-5 text-amber-700" />
                <div className="text-sm">
                  <div className="font-semibold text-amber-900">{idx >= 2 ? 'Preparing your parcel' : 'Awaiting payment / acceptance'}</div>
                  <div className="text-xs text-amber-800">A tracking number will appear here as soon as your parcel is dispatched.</div>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Delivery to</div>
                <div className="font-medium text-slate-800">{order.delivery_address?.first_name} {order.delivery_address?.last_name}</div>
                <div className="text-slate-600">{order.delivery_address?.address_line_1}</div>
                <div className="text-slate-600">{order.delivery_address?.city}, {order.delivery_address?.postcode}</div>
                <div className="text-slate-600">{order.delivery_address?.country}</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                {order.is_vat_business ? (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500">Net (ex-VAT)</span><span className="font-medium">£{(order.subtotal || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">VAT (20%)</span><span className="font-medium">£{(order.vat_amount || 0).toFixed(2)}</span></div>
                  </>
                ) : (
                  <div className="text-[11px] text-slate-500 leading-snug">All-inclusive of UK VAT &amp; Royal Mail 48 delivery.</div>
                )}
                <div className="flex justify-between font-bold pt-2 border-t border-slate-200"><span>Total</span><span className="text-emerald-700">£{(order.total || 0).toFixed(2)}</span></div>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-6 text-center">Need help with this order? Email <a href="mailto:support@iiypv.com" className="text-emerald-700 hover:underline">support@iiypv.com</a> quoting <span className="font-mono">{order.invoice_number}</span>.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
